import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { PageHeader } from "@/components/dashboard/ui";

function Bar({ label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 12 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5"><span className="text-secondary-foreground">{label}</span><span className="text-muted-foreground">{used} / {unlimited ? "∞" : limit}</span></div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function Subscription() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/subscription").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;

  const change = async (plan) => {
    setBusy(plan);
    try { await api.post("/subscription", { plan }); toast.success("Plan updated"); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const u = data.usage;
  return (
    <div>
      <PageHeader title="Subscription" subtitle="Manage your PMR Events plan and usage." />
      <div className="rounded-2xl border border-gold/20 bg-gradient-to-r from-gold/5 to-transparent p-6 mb-6 grid lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-gold">Current plan</div>
          <div className="font-serif text-3xl text-white mt-1 capitalize">{data.current_plan}</div>
        </div>
        <div className="space-y-4">
          <Bar label="Events" used={u.events} limit={u.events_limit} />
          <Bar label="Photos" used={u.photos} limit={u.photos_limit} />
          <Bar label="Storage (GB)" used={u.storage_gb} limit={u.storage_limit_gb} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-stretch">
        {data.plans.map((p) => {
          const current = p.id === data.current_plan;
          return (
            <div key={p.id} className={`relative rounded-2xl p-7 flex flex-col ${p.popular ? "border-2 border-gold gold-glow bg-gradient-to-b from-gold/8 to-transparent" : "glass border-white/8"}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-xs font-medium px-4 py-1 rounded-full">Most Popular</div>}
              <div className="text-white font-medium text-lg">{p.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.tagline}</div>
              <div className="mt-5 flex items-end gap-1"><span className="font-serif text-4xl text-white">{data.currency || "₹"}{Number(p.price).toLocaleString("en-IN")}</span><span className="text-muted-foreground mb-1">/{p.period === "month" ? "mo" : p.period}</span></div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-secondary-foreground"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              <button data-testid={`sub-plan-${p.id}`} disabled={current || busy === p.id} onClick={() => change(p.id)}
                className={`mt-6 w-full rounded-full py-3 font-medium transition-colors disabled:opacity-60 ${current ? "bg-white/10 text-white cursor-default" : p.popular ? "bg-gold text-black hover:bg-gold-light" : "glass text-white hover:border-gold/40"}`}>
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : current ? "Current plan" : `Switch to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-6">Placeholder pricing for demo. Payments (Stripe/Razorpay) can be connected later.</p>
    </div>
  );
}
